import { EuiFlexGrid, EuiFlexItem, EuiText } from '@elastic/eui'
import NetworkLineChart, { ChartProps } from './NetworkLineChart'
import { Tab, Tabs } from '@components/tabs'
import { formatTimestamp, getDataDomainByQuery } from '@services/data'
import { useEffect, useMemo } from 'react'

import { IfInView } from '@components/layout'
import { useAppSelector } from '@hooks/store'
import { useSnmpData } from '@hooks/network'
import { useTranslation } from 'react-i18next'

type SnmpTrafficChartProps = ChartProps & {
    sensorId: number
}

const interfacesHolder: string[] = []

const SnmpTrafficChart = ({
    data,
    query,
    setQuery,
    sensorId,
}: SnmpTrafficChartProps) => {
    const {
        charts,
        chartsData,
        onInterfaceChange,
        onDisabledLinesChange,
        isLoading,
    } = useSnmpData(sensorId, query)

    const { disabledLines } = useAppSelector((state) => state.network)

    const { t } = useTranslation(['common', 'network'])
    const xAxisDomain = useMemo(() => getDataDomainByQuery(query), [query])

    useEffect(() => {
        onDisabledLinesChange(disabledLines)
    }, [disabledLines])

    return (
        <>
            <div style={{ marginTop: 10 }}>
                <Tabs initialTab="bandwidth" destroyOnHide>
                    <Tab id="bandwidth" title={t('network:snmp_tab_bandwidth')}>
                        <EuiFlexGrid columns={2}>
                            {charts.bandwidth.map((chart) => (
                                <EuiFlexItem key={`chart_${chart.title}`}>
                                    <EuiFlexGrid direction="row">
                                        <EuiFlexItem>
                                            <EuiText>
                                                {`[${chart.key}] `}
                                                {`${t('common:interface')}: ${chart.title}`}
                                            </EuiText>
                                        </EuiFlexItem>
                                        <EuiFlexItem>
                                            <IfInView
                                                height={250}
                                                onChange={onInterfaceChange}
                                                viewId={chart.key.toString()}
                                                isLoading={isLoading}
                                            >
                                                <NetworkLineChart
                                                    data={chartsData}
                                                    isAnimationActive={false}
                                                    query={query}
                                                    setQuery={setQuery}
                                                    lines={chart.lines}
                                                    xAxis="timestamp"
                                                    xAxisFormatter={(v) =>
                                                        formatTimestamp(
                                                            v,
                                                            query
                                                        )
                                                    }
                                                    yAxisFormatter={(v) =>
                                                        `${v} Mbps`
                                                    }
                                                    xAxisDomain={xAxisDomain}
                                                    yAxisDomain={[
                                                        0,
                                                        chart.maxBandwidth,
                                                    ]}
                                                    height={250}
                                                />
                                            </IfInView>
                                        </EuiFlexItem>
                                    </EuiFlexGrid>
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGrid>
                    </Tab>
                    <Tab id="errors" title={t('network:snmp_tab_errors')}>
                        <EuiFlexGrid columns={2}>
                            {charts.errors.map((chart) => (
                                <EuiFlexItem key={`chart_${chart.title}`}>
                                    <EuiFlexGrid direction="row">
                                        <EuiFlexItem>
                                            <EuiText>{`${t('common:interface')}: ${chart.title}`}</EuiText>
                                        </EuiFlexItem>
                                        <EuiFlexItem>
                                            <IfInView
                                                height={250}
                                                onChange={onInterfaceChange}
                                                viewId={chart.key.toString()}
                                                isLoading={isLoading}
                                            >
                                                <NetworkLineChart
                                                    data={chartsData}
                                                    isAnimationActive={false}
                                                    query={query}
                                                    setQuery={setQuery}
                                                    lines={chart.lines}
                                                    xAxis="timestamp"
                                                    xAxisFormatter={(v) =>
                                                        formatTimestamp(
                                                            v,
                                                            query
                                                        )
                                                    }
                                                    yAxisFormatter={(v) => v}
                                                    xAxisDomain={xAxisDomain}
                                                    yAxisDomain={[
                                                        0,
                                                        chart.maxBandwidth,
                                                    ]}
                                                    height={250}
                                                />
                                            </IfInView>
                                        </EuiFlexItem>
                                    </EuiFlexGrid>
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGrid>
                    </Tab>
                    <Tab id="packets" title={t('network:snmp_tab_packets')}>
                        <EuiFlexGrid columns={2}>
                            {charts.packets.map((chart) => (
                                <EuiFlexItem key={`chart_${chart.title}`}>
                                    <EuiFlexGrid direction="row">
                                        <EuiFlexItem>
                                            <EuiText>{`${t('common:interface')}: ${chart.title}`}</EuiText>
                                        </EuiFlexItem>
                                        <EuiFlexItem>
                                            <IfInView
                                                height={250}
                                                onChange={onInterfaceChange}
                                                viewId={chart.key.toString()}
                                                isLoading={isLoading}
                                            >
                                                <NetworkLineChart
                                                    data={chartsData}
                                                    isAnimationActive={false}
                                                    query={query}
                                                    setQuery={setQuery}
                                                    lines={chart.lines}
                                                    xAxis="timestamp"
                                                    xAxisFormatter={(v) =>
                                                        formatTimestamp(
                                                            v,
                                                            query
                                                        )
                                                    }
                                                    yAxisFormatter={(v) => v}
                                                    xAxisDomain={xAxisDomain}
                                                    yAxisDomain={[
                                                        0,
                                                        chart.maxBandwidth,
                                                    ]}
                                                    height={250}
                                                />
                                            </IfInView>
                                        </EuiFlexItem>
                                    </EuiFlexGrid>
                                </EuiFlexItem>
                            ))}
                        </EuiFlexGrid>
                    </Tab>
                </Tabs>
            </div>
        </>
    )
}

export default SnmpTrafficChart
